// Shared reader for the site-wide 14-day trading-hours JSON export
// (snippets/makc-trading-hours-window.liquid, rendered from
// layout/theme.liquid) -- parsed once, here, matching the exact
// established pattern from assets/makc-catalogue.js. Trading hours are
// the "rarely change, safe to precompute server-side once" category:
// unlike cart contents or actual slot visibility (both inherently
// time-sensitive / client-mutable), the store's own opening hours for
// the next 14 days are fixed at page load and don't need to react to
// anything happening client-side.
//
// This module also owns the one shop-timezone-safe "now" a page load
// gets, plus the small set of HH:MM/minute-arithmetic helpers every
// real consumer of trading hours needs (assets/makc-time-picker.js's
// Standard-floor and Made-to-order constraint math) -- kept here rather
// than duplicated in the Time Picker, since both this file and that one
// are doing arithmetic against the exact same "minutes since midnight,
// shop time" representation.
//
// Shop-time "now": the device clock is a real instant (Date.now() is
// timezone-independent), and that instant formatted in the shop's IANA
// zone via Intl.DateTimeFormat is the shop's wall-clock time wherever
// the customer happens to be. That is what getShopNow() returns.
//
// The window snippet also embeds Liquid's 'now' as a data attribute --
// but that is the time Shopify RENDERED the HTML, not the time it is
// being viewed. Shopify serves storefront pages from its page cache, so
// the stamp can be minutes to a day behind (confirmed 2026-09-15: a
// Sunday-rendered page viewed Monday morning let a Sunday pickup slot
// through checkout). Since 2026-09-15 the stamp is NEVER a clock source
// -- not even as a fallback. If the Intl formatter can't be built, or
// throws at call time, getShopNow() falls back to the DEVICE clock
// (right for a device set to the shop's zone, at worst hours off
// elsewhere, never a day behind the way a cached stamp advanced by
// elapsed time can be). The stamp is kept for one purpose: a drift
// warning when it and the live clock disagree by more than five
// minutes, re-run on a bfcache restore. getClockSource() reports which
// clock is in use ('intl' or 'device') so a field report can be read
// from the console. The 2026-09-15 sighting of an 11am floor at 3:50pm
// after a bfcache back-navigation is most likely a document loaded
// before this module's real-clock change was deployed, restored with
// its old stamp-based clock; this module's own pageshow check exists
// so the next such sighting is diagnosable rather than inferred.
//
// Either way the returned Date object does not represent a genuine UTC
// instant -- it's a self-consistent "bag of shop-local Y-M-D-H-M-S
// values" -- which is fine and correct as long as every caller only ever
// reads calendar/clock components back out of it (getFullYear/getMonth/
// getDate/getDay/getHours/getMinutes) and never compares it against a
// genuinely UTC-anchored timestamp from elsewhere.
window.makcTradingHours = (function () {
  var windowEl = document.querySelector('[data-makc-trading-hours-window]');
  var days = [];
  if (windowEl) {
    try {
      days = JSON.parse(windowEl.textContent) || [];
    } catch (e) {
      days = [];
    }
  }

  var STALE_STAMP_WARN_MS = 5 * 60 * 1000;

  // Render-time stamp -- diagnostics only, never a clock (see the header
  // comment). Parsed once; null when absent or unparseable.
  var rawRenderStamp = windowEl ? windowEl.getAttribute('data-makc-shop-now') : null;
  var renderStamp = null;
  if (rawRenderStamp) {
    var parsedStamp = new Date(rawRenderStamp.replace(' ', 'T'));
    if (!isNaN(parsedStamp.getTime())) renderStamp = parsedStamp;
  }
  var clientLoadTimestamp = Date.now();

  // What the stamp would read now had it been fresh at load -- the
  // comparison side of the drift warning, nothing else reads it.
  function getRenderStampNow() {
    if (!renderStamp) return null;
    return new Date(renderStamp.getTime() + (Date.now() - clientLoadTimestamp));
  }

  // Real clock: Intl formatter in the shop's zone, built once. Any
  // failure (unknown zone, no Intl time-zone data) leaves it null and
  // getShopNow() uses the device clock instead.
  var shopTimeZone = windowEl ? windowEl.getAttribute('data-makc-shop-timezone') : null;
  var zoneFormatter = null;
  if (shopTimeZone && window.Intl && Intl.DateTimeFormat) {
    try {
      zoneFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: shopTimeZone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
      zoneFormatter.formatToParts(new Date());
    } catch (e) {
      zoneFormatter = null;
    }
  }

  // 'intl' while the shop-zone formatter works; 'device' if it could not
  // be built, or permanently once a call-time format throws.
  var clockSource = zoneFormatter ? 'intl' : 'device';

  function getIntlShopNow() {
    var values = {};
    zoneFormatter.formatToParts(new Date()).forEach(function (part) {
      values[part.type] = part.value;
    });
    // Some engines print midnight as "24" under hour12: false.
    var hour = parseInt(values.hour, 10) % 24;
    return new Date(
      parseInt(values.year, 10),
      parseInt(values.month, 10) - 1,
      parseInt(values.day, 10),
      hour,
      parseInt(values.minute, 10),
      parseInt(values.second, 10)
    );
  }

  // The device clock as the same "bag of local components" shape --
  // exact when the device is set to the shop's zone, and the only
  // fallback that can't be a day behind.
  function getDeviceNow() {
    return new Date();
  }

  function getShopNow() {
    if (zoneFormatter) {
      try {
        return getIntlShopNow();
      } catch (error) {
        zoneFormatter = null;
        clockSource = 'device';
        console.warn('[makc-trading-hours] shop-timezone formatting threw at call time; shop "now" is the device clock from here on', error);
      }
    }
    return getDeviceNow();
  }

  function getClockSource() {
    return clockSource;
  }

  function formatStamp(date) {
    return formatDateYMD(date) + ' ' + padTwoDigits(date.getHours()) + ':' + padTwoDigits(date.getMinutes()) + ':' + padTwoDigits(date.getSeconds());
  }

  // Clock diagnostics, run at load and again on every bfcache restore
  // (the same event assets/makc-cart.js and assets/makc-cart-shell.js
  // already reset on). Every line carries the clock source, the live
  // shop time and the raw render stamp, so a field report can state
  // which clock the page was on and how stale its HTML was. Warnings
  // only when something is off; the pageshow pass always logs one info
  // line, since a restored page is exactly the case worth a record.
  function reportClock(context) {
    var shopNow = getShopNow();
    var summary = {
      context: context,
      clockSource: clockSource,
      shopNow: formatStamp(shopNow),
      renderStamp: rawRenderStamp || null,
      shopTimeZone: shopTimeZone || null
    };
    if (context !== 'load') {
      console.info('[makc-trading-hours] clock check', summary);
    }
    if (clockSource === 'device') {
      console.warn('[makc-trading-hours] no usable shop timezone; shop "now" is the DEVICE clock, correct only on a device set to the shop\'s zone', summary);
    }
    var stampNow = getRenderStampNow();
    if (stampNow) {
      var driftMs = shopNow.getTime() - stampNow.getTime();
      if (Math.abs(driftMs) > STALE_STAMP_WARN_MS) {
        console.warn('[makc-trading-hours] page render stamp is ' + Math.round(driftMs / 60000) + ' min behind live shop time -- HTML served from cache; window day 0 may not be today. The stamp is not used as a clock.', summary);
      }
    }
  }

  reportClock('load');

  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    reportClock('pageshow');
  });

  function getWindow() {
    return days;
  }

  function getDay(dateString) {
    return (
      days.filter(function (entry) {
        return entry.date === dateString;
      })[0] || null
    );
  }

  // Manual zero-pad -- String.prototype.padStart would do this in one
  // call, but every other makc-*.js file in this codebase sticks to
  // plain pre-ES2017 syntax throughout (no arrow functions, no
  // Array.from, confirmed by grepping the rest of assets/ before
  // writing this), so this matches that established convention rather
  // than introducing the first exception.
  function padTwoDigits(value) {
    return value < 10 ? '0' + value : String(value);
  }

  // 'YYYY-MM-DD', reading the same "fake local" components getShopNow()
  // produces -- never toISOString() (that re-interprets through real
  // UTC, which would silently reintroduce the exact browser-timezone
  // dependency this module exists to avoid).
  function formatDateYMD(date) {
    var year = date.getFullYear();
    var month = padTwoDigits(date.getMonth() + 1);
    var day = padTwoDigits(date.getDate());
    return year + '-' + month + '-' + day;
  }

  // Calendar-day arithmetic via setDate (lets JS's own Date handle
  // month/year rollover) rather than adding n * 86400000ms -- since
  // these Date objects are already "fake local" bags of calendar
  // values, not real instants, setDate is the correct way to advance
  // them by whole days regardless of month length.
  function addDays(date, n) {
    var result = new Date(date.getTime());
    result.setDate(result.getDate() + n);
    return result;
  }

  // "HH:MM" (24-hour) -> minutes since midnight. Returns null for
  // null/undefined input (a closed day's opens/closes, or a product
  // with no MTO metafields set) so callers can propagate "no value"
  // rather than mis-parsing it as midnight.
  function parseHHMM(value) {
    if (value == null) {
      return null;
    }
    var parts = value.split(':');
    return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
  }

  // Minutes since midnight -> compact display label ("1pm", "1:30pm") --
  // matches the Time Picker's own existing hardcoded slot-label
  // convention (no leading zero, no ":00" on the hour, lowercase am/pm).
  function formatMinutesAsLabel(minutes) {
    var totalMinutes = ((minutes % 1440) + 1440) % 1440;
    var hour24 = Math.floor(totalMinutes / 60);
    var minute = totalMinutes % 60;
    var period = hour24 < 12 ? 'am' : 'pm';
    var hour12 = hour24 % 12;
    if (hour12 === 0) {
      hour12 = 12;
    }
    var minuteText = minute === 0 ? '' : ':' + padTwoDigits(minute);
    return hour12 + minuteText + period;
  }

  return {
    getWindow: getWindow,
    getDay: getDay,
    getShopNow: getShopNow,
    getClockSource: getClockSource,
    formatDateYMD: formatDateYMD,
    addDays: addDays,
    parseHHMM: parseHHMM,
    formatMinutesAsLabel: formatMinutesAsLabel
  };
})();
